import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle } from "lucide-react";

/**
 * Terms of Service — public legal page mounted at /terms.
 *
 * STATUS: Working draft. Drafted using standard US SaaS-Terms conventions
 * with EU consumer-rights carve-outs. Still requires sign-off from a
 * licensed attorney admitted in our jurisdiction. The "For our reviewing
 * attorney" appendix at the bottom lists the specific clauses that should
 * be verified before public launch.
 */
const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";

export default function Terms() {
  return (
    <div className="min-h-screen rm-paper" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link to="/" className="font-mono-rm uppercase text-[10px] tracking-widest inline-flex items-center gap-1 mb-4"
              style={{ color: "#475569", fontWeight: 700 }}
              data-testid="terms-back-link">
          <ArrowLeft size={11} /> Back to Resmo
        </Link>

        <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
          · Resmo · Terms of Service
        </div>
        <h1 className="font-display uppercase leading-tight mt-1"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", color: NAVY, letterSpacing: "-0.02em" }}
            data-testid="terms-title">
          Be honest. Be kind. Get hired.
        </h1>
        <p className="text-sm mt-2" style={{ color: "#475569" }}>
          Effective date: <strong>February 1, 2026</strong> · Last updated: <strong>June 4, 2026</strong> ·{" "}
          <Link to="/privacy" style={{ color: ORANGE, textDecoration: "underline" }}>See the Privacy notice →</Link>
        </p>

        {/* Working-draft banner */}
        <div className="rm-card-static p-3 mt-4"
             style={{ background: "#FEF3C7", borderColor: "#CA8A04" }}
             data-testid="terms-draft-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} style={{ color: "#78350F", marginTop: 2, flexShrink: 0 }} />
            <p className="text-[12px] leading-snug" style={{ color: "#78350F" }}>
              <strong>Working draft — pending counsel review.</strong> These Terms have been drafted using standard US SaaS conventions plus EU consumer-rights carve-outs but have not yet been reviewed by a licensed attorney in our jurisdiction. They are binding for beta users in good faith. Material changes will be emailed to subscribers with 30 days notice.
            </p>
          </div>
        </div>

        <div className="rm-card-static p-4 mt-5"
             style={{ background: "#FFF7F2", borderColor: ORANGE, boxShadow: "3px 3px 0 0 #0F1B2D" }}>
          <div className="flex items-start gap-2">
            <FileText size={18} style={{ color: ORANGE, marginTop: 2, flexShrink: 0 }} />
            <div>
              <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
                · The plain-English version
              </div>
              <ul className="text-[14px] leading-relaxed mt-1.5 space-y-1.5" style={{ color: NAVY }}>
                <li>· Resmo is a tool. The honesty of your resume is on <strong>you</strong>. Don't fabricate.</li>
                <li>· Don't submit other people's resumes without their permission.</li>
                <li>· No reverse engineering, scraping, or hammering the API.</li>
                <li>· We can suspend abusive accounts; we'll always email you first when reasonable.</li>
                <li>· You can cancel any subscription from /account.</li>
              </ul>
            </div>
          </div>
        </div>

        <Section title="1. The agreement" testid="terms-s1">
          <p>
            By creating a Resmo account, uploading a resume, or running any analysis, you (an individual user) enter into a binding agreement with Resmo (the entity defined in the Privacy notice). If you are accepting on behalf of an employer or other entity (e.g., a recruiter signing up under Resmo Pro on behalf of an agency), you represent that you have the authority to bind that entity, and references to "you" mean both you and that entity.
          </p>
          <p className="mt-2">
            If you don't agree to these Terms, do not use the service.
          </p>
        </Section>

        <Section title="2. Eligibility" testid="terms-s2">
          <p>
            You must be at least 16 years old (or the minimum age of digital consent in your jurisdiction, whichever is higher) to use Resmo. You must use Resmo for your own career — or, if you have signed up as a Resmo Pro recruiter, on behalf of candidates who have explicitly linked their Resmo account to yours via the in-app Acting-on-behalf flow. Each individual user must have their own account; account-sharing is not permitted.
          </p>
        </Section>

        <Section title="3. The honesty floor (the core deal)" testid="terms-s3">
          <p>
            Resmo will rewrite, restructure, and polish your resume. Resmo will <strong>not</strong> invent achievements you didn't have. Our Honesty Check feature is the contract: Resmo flags any line in a tailored resume that doesn't trace back to your uploaded base resume, and you decide what to keep, revise, or remove. If you upload a fabricated base resume, the entire tailored output is fabricated and that is your responsibility, not Resmo's. The Verified by Resmo seal is only issued when every flagged claim has been personally approved by you.
          </p>
          <p className="mt-2">
            You agree not to use Resmo's output to misrepresent yourself to any employer or recruiter.
          </p>
        </Section>

        <Section title="4. Subscriptions, credits, refunds" testid="terms-s4">
          <p>Resmo offers two paid tiers:</p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li><strong>Pro · $29/month</strong> — unlimited candidate-side tailoring, briefs, playbooks, and Honesty Checks.</li>
            <li><strong>Resmo Pro · Recruiter · $59/month</strong> — Matchmaker requisitions, multi-client roster, Acting-on-behalf across the toolkit, and all candidate Pro features.</li>
          </ul>
          <p className="mt-2">
            Both subscriptions auto-renew monthly on the day of the month you first subscribed. You can cancel at any time from /account → Billing. Cancellations stop the next renewal; the current period remains active until expiry, and we do not pro-rate refunds for partial months.
          </p>
          <p className="mt-2">
            <strong>Beta refund window</strong>: if you cancel within 7 days of your first charge and have used Resmo fewer than 3 times (any combination of tailored resumes, briefs, playbooks, or matchmaker runs), email <strong>billing@resmo.app</strong> for a full refund.
          </p>
          <p className="mt-2">
            Credit packs are non-refundable once any credit from the pack has been consumed. Unused credits never expire while your account is active.
          </p>
          <p className="mt-2">
            <strong>EU/UK consumer right of withdrawal</strong>: if you are a consumer based in the EU/UK, you have a 14-day right to withdraw from the subscription contract (Consumer Rights Directive 2011/83/EU). By starting to use Resmo within that 14-day window you expressly agree to begin performance of the service and acknowledge that you waive your right of withdrawal once the digital service has been used.
          </p>
        </Section>

        <Section title="5. Acceptable use" testid="terms-s5">
          <p>You agree NOT to:</p>
          <ul className="list-disc ml-5 space-y-1.5 mt-2">
            <li>Upload anyone else's resume without their explicit, documented permission.</li>
            <li>Scrape Resmo content, models, prompts, templates, or scoring outputs.</li>
            <li>Reverse engineer the API, the prompts, the integrity rubric, or any other internal logic.</li>
            <li>Use the service to harass, defame, or discriminate against any person or group.</li>
            <li>Use the Matchmaker to spam candidates — daily caps apply, and repeated abuse leads to a permanent ban without refund.</li>
            <li>Misrepresent yourself as a recruiter, agency, employer, or candidate.</li>
            <li>Use Resmo to generate misleading or deceptive content (fake claims, exaggerated titles, fabricated employers).</li>
            <li>Use the service in violation of any applicable export-control, sanctions, or anti-bribery law.</li>
            <li>Probe, scan, or test the vulnerability of the service except via a documented coordinated-disclosure process with our security team (security@resmo.app).</li>
          </ul>
        </Section>

        <Section title="6. Your content & license" testid="terms-s6">
          <p>
            You retain ownership of all content you upload to Resmo (your resume, job descriptions, custom prompts, etc.). You grant us a worldwide, non-exclusive, royalty-free license to host, process, transmit to sub-processors (see Privacy §4), and modify that content solely for the purpose of providing the service to you. This license terminates when you delete the content or your account. We do not use your content to train our own models or any third-party model.
          </p>
        </Section>

        <Section title="7. Our intellectual property" testid="terms-s7">
          <p>
            Resmo's branding, code, templates, mascot illustrations, scoring rubrics, integrity prompts, and the Verified by Resmo seal artwork are the property of Resmo. We grant you a limited, non-exclusive, non-transferable license to use them only for displaying the outputs the service produces for you, including embedding the Verified by Resmo seal on a portfolio website that links back to a Resmo share URL. Any other use requires our prior written consent.
          </p>
        </Section>

        <Section title="8. Service availability & beta status" testid="terms-s8">
          <p>
            Resmo is currently in private beta and is provided "<strong>as is</strong>" and "<strong>as available</strong>", without warranties of any kind, whether express, implied, statutory, or otherwise — including without limitation any warranty of merchantability, fitness for a particular purpose, title, or non-infringement. We aim for 99% uptime but do not guarantee it during the beta. We may add, change, or remove features at any time without notice.
          </p>
        </Section>

        <Section title="9. Limitation of liability" testid="terms-s9">
          <p>
            <strong>To the maximum extent permitted by applicable law</strong>: in no event will Resmo, its affiliates, officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, punitive, or exemplary damages, including without limitation lost profits, lost interview opportunities, lost employment, loss of data, loss of goodwill, or substitute service costs, even if we have been advised of the possibility of such damages.
          </p>
          <p className="mt-2">
            Our aggregate liability under or in connection with these Terms, whether in contract, tort (including negligence), strict liability, or otherwise, will not exceed the greater of (a) the total amount you paid us in the twelve months preceding the event giving rise to the claim, or (b) one hundred US dollars ($100).
          </p>
          <p className="mt-2">
            <strong>Consumer carve-out</strong>: nothing in this section limits liability that cannot be limited or excluded under applicable consumer-protection law, including, for EU/UK consumers, liability for gross negligence, willful misconduct, death, or personal injury caused by our negligence.
          </p>
        </Section>

        <Section title="10. Indemnification" testid="terms-s10">
          <p>
            You agree to indemnify, defend, and hold harmless Resmo and its affiliates from and against any third-party claims, damages, and expenses (including reasonable legal fees) arising from (a) your breach of these Terms, (b) your violation of any law or any third-party right (including intellectual-property rights and privacy rights), (c) your misuse of Resmo to misrepresent yourself to an employer, or (d) any content you uploaded that contained someone else's personal data without their permission.
          </p>
        </Section>

        <Section title="11. Termination" testid="terms-s11">
          <p>
            You can terminate this agreement at any time by deleting your account at /account → Delete account. We may suspend or terminate accounts that materially breach these Terms; we will give reasonable prior notice by email except where the breach involves security risk, criminal conduct, or imminent harm to others.
          </p>
          <p className="mt-2">
            On termination, your right to use the service stops immediately, your active subscription is cancelled (no further charges), and your data is deleted in accordance with the Privacy notice. Sections that by their nature should survive termination (intellectual property, limitation of liability, indemnification, governing law, dispute resolution) will survive.
          </p>
        </Section>

        <Section title="12. Electronic communications & consent to e-sign" testid="terms-s12">
          <p>
            By using the service you consent to receive communications from us in electronic form (in-app notifications, email). You agree that electronic notices, agreements, and disclosures satisfy any legal requirement that such communications be in writing. You also consent to the use of electronic signatures (your account creation and continued use constitute your signature on this agreement).
          </p>
        </Section>

        <Section title="13. Governing law & dispute resolution" testid="terms-s13">
          <p>
            These Terms are governed by the laws of the State of Delaware, USA, without regard to its conflict-of-laws rules. The parties consent to the exclusive personal jurisdiction and venue of the state and federal courts located in New Castle County, Delaware for any dispute not subject to arbitration or small claims.
          </p>
          <p className="mt-2">
            <strong>For EU/UK consumers</strong>: nothing in this clause deprives you of the protection of the mandatory consumer-protection laws of your country of residence, including the right to bring proceedings in your local courts.
          </p>
          <p className="mt-2">
            <strong>Informal dispute resolution</strong>: before filing any formal claim, you agree to first contact us at <strong>legal@resmo.app</strong> and attempt to resolve the dispute informally for at least 30 days. We commit to engaging in good faith.
          </p>
        </Section>

        <Section title="14. Force majeure" testid="terms-s14">
          <p>
            Neither party is liable for failure to perform due to causes beyond its reasonable control, including but not limited to acts of God, war, terrorism, civil unrest, government action, network outages, third-party infrastructure failures (e.g., AWS, MongoDB Atlas, Stripe, an AI vendor), labor disputes, or pandemics.
          </p>
        </Section>

        <Section title="15. Severability & no waiver" testid="terms-s15">
          <p>
            If any provision of these Terms is held invalid or unenforceable, that provision will be enforced to the maximum extent permissible, and the remaining provisions will remain in full force. Our failure to enforce any provision does not waive our right to enforce it later.
          </p>
        </Section>

        <Section title="16. Entire agreement & assignment" testid="terms-s16">
          <p>
            These Terms, together with the Privacy notice and any in-app billing-tier-specific terms shown at checkout, constitute the entire agreement between you and Resmo with respect to the service. You may not assign these Terms; we may assign them to a successor in connection with a merger, acquisition, or sale of substantially all of our assets, provided the successor honors the Privacy notice.
          </p>
        </Section>

        <Section title="17. Changes" testid="terms-s17">
          <p>
            We will notify subscribers by email at least 30 days before material changes to these Terms take effect. Material changes include, without limitation: price increases, new restrictions on use, additions to the limitation of liability, or changes to the governing-law or dispute-resolution sections. Continued use after a material change becomes effective constitutes acceptance.
          </p>
        </Section>

        <Section title="18. Contact" testid="terms-s18">
          <p>
            Legal: <strong>legal@resmo.app</strong> · Billing: <strong>billing@resmo.app</strong> · Security disclosure: <strong>security@resmo.app</strong> · Everything else: <strong>hello@resmo.app</strong>.
          </p>
        </Section>

        {/* Reviewer's appendix */}
        <details className="mt-8 rm-card-static p-3"
                 style={{ background: "#F1F5F9", borderColor: "#94A3B8" }}
                 data-testid="terms-counsel-appendix">
          <summary className="font-mono-rm uppercase text-[10px] tracking-widest cursor-pointer"
                   style={{ color: "#475569", fontWeight: 700 }}>
            · For our reviewing attorney
          </summary>
          <div className="text-[12px] leading-snug mt-2" style={{ color: NAVY }}>
            <p>Specific items we'd appreciate verification on:</p>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              <li>Confirm Delaware governing law is the right choice for our entity; revisit if we re-domicile.</li>
              <li>Decide whether to add a mandatory arbitration / class-action waiver (currently absent — informal-resolution-then-courts model). FAA enforceability + AAA consumer rules trade-off.</li>
              <li>EU/UK consumer-protection carve-outs in §9, §11, §13 — confirm phrasing is sufficient.</li>
              <li>EU/UK 14-day right of withdrawal language in §4 — confirm we can rely on the "performance starts" waiver under Art. 16(m) of the CRD.</li>
              <li>Liability cap of $100 — confirm enforceable for paid users; consider raising to "12 months of fees" for the recruiter Pro tier ($708 cap at $59/mo).</li>
              <li>Confirm the indemnification clause is mutual where required by some jurisdictions (currently one-way).</li>
              <li>Confirm Resmo's branding/IP license-back clause in §7 (allowing seal embed on third-party portfolio sites) doesn't accidentally permit broader sub-licensing.</li>
              <li>Confirm the "30 days notice for material change" obligation is consistent with our marketing-email opt-out scope.</li>
              <li>Confirm export-control / sanctions language in §5 covers our customer base.</li>
              <li>Decide whether to add a US Government Restricted Rights clause (FAR 12.212 / DFARS) if we ever target federal candidates.</li>
            </ol>
          </div>
        </details>

        <p className="text-[11px] mt-4" style={{ color: "#94A3B8" }}>
          © 2026 Resmo. Working draft pending counsel review. By using the service you accept these Terms.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mt-6" data-testid={testid}>
      <h2 className="font-display uppercase text-lg leading-tight mb-2"
          style={{ color: NAVY, letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      <div className="text-[14px] leading-relaxed" style={{ color: NAVY }}>
        {children}
      </div>
    </section>
  );
}
